<script setup lang="ts">
import { InputAutoCompletion, SancareOcticon } from '@sancare/typescript-commons'
import type { CompletionOption } from '@sancare/typescript-commons/src/components/types'
import {
  useAutoCompletionDebouncedWatcher
} from '@sancare/typescript-commons/src/misc/composables/useAutoCompletionDebouncedWatcher'
import type { Finess, FinessWithoutLongDescription } from '@sancare/typescript-commons/src/types/finess'
import _ from 'lodash'
import { computed, onMounted, ref, toRef, watch } from 'vue'
import { useStore } from 'vuex'

export interface FinessInputProps {
  type: string
  userId: number
  userFinessList: Finess[]
  completionFinessList: CompletionOption<FinessWithoutLongDescription>[]
}
const props = defineProps<FinessInputProps>()

const store = useStore()

const showCompletion = ref(false)
const finessInput = ref('')
const requestError = ref(null)

const label = computed(() =>
  props.type === 'legal' ? 'FINESS juridique' : 'FINESS géographique'
)

const assignFinessRequestName = computed(() =>
  props.type === 'legal' ? 'assignLegalFiness' : 'assignGeoFiness'
)

const unassignFinessRequestName = computed(() =>
  props.type === 'legal' ? 'unassignLegalFiness' : 'unassignGeoFiness'
)

const searchFinessRequestName = computed(() =>
  props.type === 'legal' ? 'searchLegalFiness' : 'searchGeoFiness'
)

const clearFinessListRequestName = computed(() =>
  props.type === 'legal' ? 'searchLegalFinessClear' : 'searchGeoFinessClear'
)

const uniqueId = _.uniqueId('finessInput_')

const assignFinessRequest = computed(() =>
  props.type === 'legal'
    ? store.state.admin.assignLegalFinessRequest
    : store.state.admin.assignGeoFinessRequest
)

const unassignFinessRequest = computed(() =>
  props.type === 'legal'
    ? store.state.admin.unassignLegalFinessRequest
    : store.state.admin.unassignGeoFinessRequest
)

const searchFinessRequest = computed(() =>
  props.type === 'legal'
    ? store.state.admin.searchLegalFiness
    : store.state.admin.searchGeoFiness
)

watch(assignFinessRequest, (req) => {
  requestError.value = req.error
})

watch(unassignFinessRequest, (req) => {
  requestError.value = req.error
})

const assignFiness = (finess) => {
  if (finess) {
    store.dispatch(assignFinessRequestName.value, { userId: props.userId, finess: finess })
    finessInput.value = null
  }
}

const unassignFiness = (finess: string) => {
  store.dispatch(unassignFinessRequestName.value, { userId: props.userId, finess: finess })
}

const openCompletion = () => {
  showCompletion.value = true
}

const closeCompletion = () => {
  showCompletion.value = false
}

const selectFiness = (finess: Finess) => {
  finessInput.value = finess.finess
  showCompletion.value = false
}

const fetchFinessData = (search: string, signal: AbortSignal) => {
  store.dispatch(searchFinessRequestName.value, { search, signal })
}

const clearFinessData = () => {
  store.commit(clearFinessListRequestName.value)
}

onMounted(() => {
  const completionFinessListRef = toRef(props, 'completionFinessList')

  useAutoCompletionDebouncedWatcher(
    finessInput,
    completionFinessListRef,
    (search, signal) => fetchFinessData(search, signal),
    () => clearFinessData()
  )
})
</script>

<template>
  <div class="settings-table__data-row">
    <div class="row g-0">
      <label
        class="col-6 col-form-label"
        :for="uniqueId"
      >
        {{ label }}
      </label>
      <div class="input-group col">
        <input
          :id="uniqueId"
          v-model="finessInput"
          v-on-click-outside="closeCompletion"
          class="form-control"
          type="text"
          @click="openCompletion"
          @keydown.esc="closeCompletion"
          @keyup.enter="assignFiness(finessInput)"
        >
        <!-- @vue-generic {FinessWithoutLongDescription} -->
        <button
          class="btn btn-primary"
          type="button"
          @click="assignFiness(finessInput)"
        >
          Ok
        </button>
      </div>
    </div>
    <div class="row g-0">
      <div class="col-6 ms-auto">
        <input-auto-completion
          v-show="showCompletion && finessInput !== ''"
          :completion-options="completionFinessList"
          :is-loading="searchFinessRequest.fetching"
          @select-option="selectFiness"
        />
      </div>
    </div>

    <div class="col">
      <div
        v-if="requestError"
        class="text-danger"
      >
        {{ requestError }}
      </div>
    </div>

    <div
      v-for="finess in userFinessList"
      :key="finess['finess']"
      class="text-bg-primary badge col-auto mx-1 mb-1 ps-2 pe-2"
    >
      <span>{{ finess['shortDescription'] }} ({{ finess['finess'] }})</span>
      <span
        class="criteria-remove"
        @click="unassignFiness(finess['finess'])"
      >
        <sancare-octicon
          name="x"
          :width="14"
          :height="11"
        />
      </span>
    </div>
  </div>
</template>
